import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { KeywordTagsInput, addKeywords } from "@/components/autoreplies/KeywordTagsInput";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const CHANNEL_LABEL = { both: "WhatsApp e Instagram", whatsapp: "WhatsApp", instagram: "Instagram" };
const EMPTY = { keywords: [], response: "", button_label: "", button_url: "", channel: "both", active: true };
const URL_RE = /^https?:\/\/[^\s/$.?#].[^\s]*$/i;
export const BUTTON_MAX = 20;

export const AutoReplyForm = ({ open, onOpenChange, editing, pending, onSubmit }) => {
  const [form, setForm] = useState(EMPTY);
  const [draft, setDraft] = useState("");
  useEffect(() => {
    if (open) setForm(editing ? { keywords: editing.keywords, response: editing.response, button_label: editing.button_label || "", button_url: editing.button_url || "", channel: editing.channel, active: editing.active } : EMPTY);
    setDraft("");
  }, [open, editing]);
  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));
  const keywords = draft.trim() ? addKeywords(form.keywords, draft) : form.keywords;
  const label = form.button_label.trim();
  const link = form.button_url.trim();
  const buttonError = label && !link ? "Añade la URL destino del botón"
    : link && !label ? "Añade el texto del botón"
    : link && !URL_RE.test(link) ? "La URL debe empezar por http:// o https://" : "";
  const valid = keywords.length > 0 && form.response.trim() && !buttonError;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[480px] overflow-y-auto" data-testid="auto-reply-form">
        <SheetHeader>
          <SheetTitle>{editing ? "Editar respuesta automática" : "Nueva respuesta automática"}</SheetTitle>
          <SheetDescription>Si el mensaje del paciente contiene alguna de estas palabras o frases, el bot responderá exactamente con este texto, sin usar IA.</SheetDescription>
        </SheetHeader>
        <form className="space-y-5 mt-6" onSubmit={(e) => { e.preventDefault(); if (valid) onSubmit({ ...form, keywords, button_label: label, button_url: link }); }}>
          <div>
            <Label className="mb-1.5 block">Palabras o frases clave</Label>
            <KeywordTagsInput value={form.keywords} onChange={set("keywords")} draft={draft} onDraftChange={setDraft} />
            <p className="text-xs text-[#8A7E70] mt-1">Pulsa Intro o coma para añadir cada una. Basta con que el mensaje contenga cualquiera de ellas. Sin distinguir mayúsculas ni tildes. Se busca como palabra completa («precio» no coincide con «precioso»).</p>
          </div>
          <div>
            <Label className="mb-1.5 block">Respuesta exacta</Label>
            <Textarea data-testid="auto-reply-response-input" value={form.response} rows={6} maxLength={2000} className="resize-none"
              placeholder="Escribe el mensaje que enviará el bot. Puedes incluir emojis 😊" onChange={(e) => set("response")(e.target.value)} />
          </div>
          <div className="rounded-xl border border-[#E7E2DA] p-4 space-y-3" data-testid="auto-reply-button-fields">
            <div>
              <div className="text-sm font-medium text-[#1A1A1A]">Botón con enlace <span className="text-[#A79E90] font-normal">(opcional)</span></div>
              <p className="text-xs text-[#8A7E70] mt-0.5">El paciente verá un botón con este texto en lugar de la URL completa.</p>
            </div>
            <div>
              <Label className="mb-1.5 block">Texto del botón</Label>
              <Input data-testid="auto-reply-button-label-input" value={form.button_label} maxLength={BUTTON_MAX} placeholder="Ej.: Reservar cita, Ver PDF, Acceder aquí"
                onChange={(e) => set("button_label")(e.target.value)} />
              <p className="text-[11px] text-[#A79E90] mt-1 text-right">{form.button_label.length}/{BUTTON_MAX}</p>
            </div>
            <div>
              <Label className="mb-1.5 block">URL destino</Label>
              <Input data-testid="auto-reply-button-url-input" type="url" value={form.button_url} maxLength={1000} placeholder="https://"
                onChange={(e) => set("button_url")(e.target.value)} />
            </div>
            {buttonError && <p data-testid="auto-reply-button-error" className="text-xs text-red-600">{buttonError}</p>}
          </div>
          <div>
            <Label className="mb-1.5 block">Canal</Label>
            <Select value={form.channel} onValueChange={set("channel")}>
              <SelectTrigger data-testid="auto-reply-channel-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CHANNEL_LABEL).map(([v, l]) => (
                  <SelectItem key={v} value={v} data-testid={`auto-reply-channel-${v}`}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center justify-between rounded-xl border border-[#E7E2DA] px-4 py-3 cursor-pointer">
            <span className="text-sm font-medium text-[#1A1A1A]">{form.active ? "Activo" : "Inactivo"}</span>
            <Switch data-testid="auto-reply-active-switch" checked={form.active} onCheckedChange={set("active")} />
          </label>
          <SheetFooter>
            <Button data-testid="auto-reply-save-btn" type="submit" disabled={!valid || pending} className="w-full h-11 bg-[#1A1A1A] hover:bg-black text-white">
              {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Guardar cambios" : "Crear respuesta automática"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
};
